import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, MessageSquare, Clock, Send, Loader2 } from 'lucide-react';
import apiClient from '../services/api';

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await apiClient.post('/enquiries/contact', formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit contact form:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">{t('contactUsTitle')}</h1>
            <p className="text-xl text-muted-foreground">{t('contactUsSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h2 className="text-xl font-semibold text-foreground mb-6">{t('sendUsMessage')}</h2>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t('messageSentTitle')}</h3>
                  <p className="text-muted-foreground">{t('messageSentDesc')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                      {error}
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('nameLabel')}</label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="mt-1 border-2 border-emerald-500/40" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('emailLabel')}</label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="mt-1 border-2 border-emerald-500/40" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('subjectLabel')}</label>
                    <Input value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required className="mt-1 border-2 border-emerald-500/40" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('messageLabel')}</label>
                    <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required className="mt-1 border-2 border-emerald-500/40" rows={5} />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : t('sendMessageBtn')}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('emailLabel')}</h3>
                    <a href="mailto:support@fundthehope.com" className="text-emerald-500 hover:underline">support@fundthehope.com</a>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-teal-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('liveChat')}</h3>
                    <p className="text-muted-foreground">{t('liveChatHours')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('responseTime')}</h3>
                    <p className="text-muted-foreground">{t('responseTimeDesc')}</p>
                  </div>
                </div>
              </div>
              
              {/* Department Emails */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-4">Department Contacts</h3>
                <div className="grid gap-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Partnerships</span>
                    <a href="mailto:partnership@fundthehope.com" className="text-emerald-500 hover:underline">partnership@fundthehope.com</a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Press & Media</span>
                    <a href="mailto:press@fundthehope.com" className="text-emerald-500 hover:underline">press@fundthehope.com</a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Billing</span>
                    <a href="mailto:billing@fundthehope.com" className="text-emerald-500 hover:underline">billing@fundthehope.com</a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Legal</span>
                    <a href="mailto:legal@fundthehope.com" className="text-emerald-500 hover:underline">legal@fundthehope.com</a>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Security</span>
                    <a href="mailto:security@fundthehope.com" className="text-emerald-500 hover:underline">security@fundthehope.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
